/**
 * Best-effort extraction of a human-readable message from an API response body.
 *
 * Handles the shapes Bifrost returns (in priority order):
 *   1. Nested `error.message` — e.g. `{ error: { code, message: "..." } }`
 *   2. Nested `error.code` — e.g. `{ error: { code: "Session not found", message: "" } }`
 *   3. Top-level `message`  — e.g. `{ message: "Email already registered" }`
 *
 * Returns `null` when none of the above produce a non-empty string, so callers
 * can fall back to a generated summary like `API Error: /endpoint → 404`.
 */
export const extractBodyMessage = (body: unknown): string | null => {
  if (body === null || typeof body !== "object") {
    return null;
  }

  const record = body as Record<string, unknown>;

  if (record.error !== null && typeof record.error === "object") {
    const nested = record.error as Record<string, unknown>;
    if (typeof nested.message === "string" && nested.message !== "") {
      return nested.message;
    }
    if (typeof nested.code === "string" && nested.code !== "") {
      return nested.code;
    }
  }

  if (typeof record.message === "string" && record.message !== "") {
    return record.message;
  }

  return null;
};
